import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import FolderOutlined from '@ant-design/icons/FolderOutlined';
import { WidgetType } from '@kemu-io/kemu-core/types';
import PushpinOutlined from '@ant-design/icons/lib/icons/PushpinOutlined';
import PushpinFilled from '@ant-design/icons/lib/icons/PushpinFilled';
import { LoadingOutlined } from '@ant-design/icons';
import styles from './WidgetHelpPopup.module.css';
import MarkdownViewer from '@components/MarkdownViewer/MarkdownViewer';
import { isDevEnvironment, getNativeWidgetPublicUrl } from '@common/utils';
import useSelectMdFile from '@hooks/useSelectMdFile';
import useTranslation from '@hooks/useTranslation';
import { ApplicationContext } from '@common/context/ApplicationContext/ApplicationContext';

type Props = {
  type: WidgetType;
  title: string;
  onKeepOpen?: (keepOpen: boolean) => void;
}


const WidgetHelpPopup = (props: Props) => {
  const { type, title, onKeepOpen } = props;
  const { language } = useContext(ApplicationContext);
  const cW = useTranslation('CommonWords');
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inDevMode = isDevEnvironment();
  const loadFile = useSelectMdFile();
  const [keepOpen, setKeepOpen] = useState(false);

  const fetchHelp = useCallback(async () => {
    setLoading(true);
    const help = await fetch(getNativeWidgetPublicUrl(type, `/docs/widgets/${type}/preview-${language}.md`));

    if (!help.ok) {
      setError(`Failed to fetch help file: ${help.statusText}`);
    } else {
      const text = await help.text();
      setContent(text);
    }

    setLoading(false);
  }, [type, language]);

  const handleFileSelection = async () => {
    const { file } = await loadFile();
    if (file) {
      setError(null);
      setContent(file.content);
      setLoading(false);
    }
  };

  const handlePin = () => {
    setKeepOpen((current) => {
      const newState = !current;
      onKeepOpen?.(newState);
      return newState;
    });
  };

  useEffect(() => {
    fetchHelp();
  }, [fetchHelp]);

  return (
    <div className={styles.Wrapper}>
      <div className={styles.Header}>
        <h3>{title}</h3>
        {inDevMode && (
          <Button
            icon={<FolderOutlined />}
            size="small"
            onClick={handleFileSelection}
            title="Select markdown file"
            className={styles.FolderButton}
          />
        )}
        <Button
          icon={keepOpen ? <PushpinFilled /> : <PushpinOutlined />}
          size="small"
          onClick={handlePin}
          title="Pin"
          className={styles.PinButton}
        />
      </div>
      {loading ? (
        <div className={styles.Loading}>
          {cW('Loading')}...
          <Spin indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <>
          {error && <div className={styles.Error}>{error}</div>}
          {content && (
            <MarkdownViewer
              markdown={content || ''}
              docsUrl={`platform/docs`}
              disablePermalink
            />
          )}
        </>
      )}
    </div>
  );
};

export default WidgetHelpPopup;
